import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef,} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {createDefaultDialogConfig} from '../common/dialog-utils';
import {PaypalConfig} from '../models/paypal-config.model';
import {coursePurchased} from '../store/course.actions';
import {PaypalService} from '../services/paypal.service';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {TrackingService} from '../services/tracking.service';
import {CourseCoupon} from '../models/coupon.model';
import {CoursePageData} from '../course-page/course-page-data.model';
import {UrlBuilderService} from '../services/url-builder.service';
import {DurationPipe} from '../shared/pipes/duration.pipe';
import {TranslateModule} from '@ngx-translate/core';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {NgIf} from '@angular/common';


const PAYPAL_BUTTON_ID = "paypal-button"

declare const paypal;

export interface PaypalDialogData extends CoursePageData{
  coupon?: CourseCoupon;
}

@Component({
    selector: "paypal-dialog",
    templateUrl: "paypal-dialog.component.html",
    styleUrls: ["paypal-dialog.component.scss"],
    standalone: true,
    imports: [
        NgIf,
        MatProgressSpinnerModule,
        TranslateModule,
        DurationPipe,
    ],
})
export class PaypalDialogComponent implements OnInit {

  buttonInitializationOngoing = false;
  buttonsInitialized = false;

  constructor(
    private dialogRef: MatDialogRef<PaypalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaypalDialogData,
    private paypal:PaypalService,
    private store: Store<AppState>,
    private tracking: TrackingService,
    private messages: MessagesService,
    private loading: LoadingService,
    private snackBar: MatSnackBar,
    public ub: UrlBuilderService) {

  }

  ngOnInit() {
    this.setupPaypalButton(this.data.tenant?.paypalConfig);
  }

  onClose() {
    this.dialogRef.close();
  }

  private setupPaypalButton(paypalConfig: PaypalConfig) {

    if (this.buttonInitializationOngoing) {
      console.log(`Button ${PAYPAL_BUTTON_ID} initialization ongoing, exiting ...`);
      return;
    }

    this.buttonInitializationOngoing = true;

    console.log(`Called setupPaypalButton() for ${PAYPAL_BUTTON_ID}, Paypal button initialization ongoing...`);

    // we are going to wait until the Paypal script is fully loaded and the Paypal button is in the DOM,
    // before intializing the Paypal button
    const intervalId = setInterval(() => {

      console.log(`Calling interval script for ${PAYPAL_BUTTON_ID}...`);

      // prevent duplicate initialization of PayPal button
      if (this.buttonsInitialized) {
        console.log(`Paypal button already initialized for ${PAYPAL_BUTTON_ID}.`);
        return;
      }

      if (typeof paypal != "object") {
        console.log(`Paypal script not ready, waiting ${PAYPAL_BUTTON_ID} ...`);
      }
      else {

        console.log(`Initializing Paypal button ${PAYPAL_BUTTON_ID}...`);

        this.buttonsInitialized = true;

        clearInterval(intervalId);

        const button = document.getElementById(PAYPAL_BUTTON_ID);

        if (button) {

          console.log(`Found Paypal button ${PAYPAL_BUTTON_ID} in the DOM, setting up Paypal: `, paypalConfig);

          const coursePrice = this.data.coupon && this.data?.couponValid ? this.data.coupon.price : this.data.course.price;

          paypal.Buttons({
            createOrder: async (data, actions) => {

              const session = await this.paypal.initPaypalPurchaseCourseSession(this.data.user.id, this.data.course.id, this.data.coupon ? this.data.coupon.id : null, coursePrice).toPromise();

              this.tracking.reportStudentInitiateCheckout();

              return actions.order.create({
                purchase_units: [{
                  reference_id: session.id,
                  amount: {
                    value: coursePrice
                  }
                }],
                payee: {
                  email_address: paypalConfig.paypalEmail,
                  merchant_id: paypalConfig.paypalMerchantId
                }
              });
            },
            onCancel: (data) => {
              this.messages.i18nError("course.courseActionButtons.messages.thePaymentWasCanceled")
              this.dialogRef.close();
            },
            onError: (err) => {
              this.messages.i18nError("course.courseActionButtons.messages.paymentFailedPleaseCheckYourCardBalance")
              this.dialogRef.close();
            },
            onApprove: (data, actions) => {

              console.log(`Paypal response data: ${JSON.stringify(data)}`);

              return actions.order.capture().then((details) => {

                console.log(`Paypal order details: ${JSON.stringify(details)}`);

                console.log(`Transaction completed by ${details.payer.name.given_name}`);

                const confirmPurchase$ = this.paypal.confirmCoursePurchase(this.data.course.id, details.id, details?.purchase_units[0]?.reference_id);

                this.loading.showLoaderUntilCompleted(confirmPurchase$)
                  .subscribe(
                    () => {
                      this.store.dispatch(coursePurchased({courseId: this.data.course.id}));
                      this.messages.clear();
                      // this.messages.info("Payment successful, click on any lesson to watch your course!");
                      this.messages.i18nInfo("course.courseActionButtons.messages.paymentSuccessfulClickOnAnyLessonToWatch")


                      this.tracking.reportStudentPurchase(this.data.course.price);
                      this.dialogRef.close();
                    },
                    err => {
                      console.log("Error occurred confirming Paypal purchase: ", err);
                      // this.messages.error("Error: could not confirm Paypal purchase.");
                      this.messages.i18nError("course.courseActionButtons.messages.errorCouldNotConfirmPaypalPurchase")
                      this.dialogRef.close();
                    }
                  );

              });
            }
          }).render("#" + PAYPAL_BUTTON_ID);

        }
      }

    }, 1000);

  }
}

export function showPaypalDialog(dialog: MatDialog, data: PaypalDialogData) {
  const config = createDefaultDialogConfig();

  config.width = "950px";

  config.maxHeight = "95vh";
  config.maxWidth = "950px";

  config.data = data;

  const dialogRef = dialog.open(PaypalDialogComponent, config);

  return dialogRef
    .afterClosed()
    .pipe
    // filter(result => result && result["confirm"]),
    ();
}
